/**
 * Reusable form input component with validation and formatting support.
 *
 * @remarks Provides a standardized text input component that integrates with
 * Angular reactive forms. Supports validation error display, automatic formatting,
 * and dynamic enable/disable states. Handles various input types including text,
 * email, password, number, and telephone inputs.
 */

import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { ControlConfig } from '../../types/form.types';

/**
 * Reusable form input component with validation and formatting support.
 *
 * @remarks Provides a standardized text input component that integrates with
 * Angular reactive forms. Supports validation error display, automatic formatting,
 * and dynamic enable/disable states. Handles various input types including text,
 * email, password, number, and telephone inputs.
 */
@Component({
  selector: 'app-form-input',
  templateUrl: './form-input.component.html',
  styleUrls: ['./form-input.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SvgIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormInputComponent implements OnInit, OnChanges {
  /** @remarks Configuration object defining the input's behavior and appearance */
  @Input() controlConfig: ControlConfig;

  /** @remarks The parent form group that contains this input's form control */
  @Input() formGroup: FormGroup;

  /** @remarks External error state flag for additional validation scenarios */
  @Input() hasError: boolean = false;

  /**
   * Determines if the input is in an invalid state.
   *
   * @remarks Checks if the form control is invalid and has been interacted with
   * (touched or dirty) or if an external error flag is set.
   *
   * @readonly
   * @returns {boolean} True if the input should display as invalid
   */
  get isInvalid(): boolean {
    const control = this.formGroup?.get(this.controlConfig.name);
    return (control?.invalid && (control?.touched || control?.dirty)) || this.hasError;
  }

  /**
   * Determines if the input is in a valid state.
   *
   * @remarks Checks if the form control is valid and has been interacted with
   * (touched or dirty) and no external error flag is set.
   *
   * @readonly
   * @returns {boolean} True if the input should display as valid
   */
  get isValid(): boolean {
    const control = this.formGroup?.get(this.controlConfig.name);
    return control?.valid && (control?.touched || control?.dirty) && !this.hasError;
  }

  /**
   * Gets the placeholder text for the input.
   *
   * @remarks Returns the configured placeholder text or an empty string if none is set.
   *
   * @readonly
   * @returns {string} The placeholder text to display
   */
  get placeholder(): string {
    return this.controlConfig.placeholder || '';
  }

  /**
   * Gets the appropriate error message to display.
   *
   * @remarks Determines which error message to show based on the validation state.
   * Returns the required field message if the field is required and empty,
   * otherwise returns the general error message.
   *
   * @readonly
   * @returns {string | null} The error message to display or null if no error
   */
  get errorMessage(): string | null {
    if (this.isInvalid) {
      // check if the input is required and if the value is empty
      if (this.controlConfig.required && this.formGroup?.get(this.controlConfig.name)?.value === '') {
        return this.controlConfig.errorMessageRequired || '';
      }
      return this.controlConfig.errorMessage || '';
    }
    return null;
  }

  /**
   * Component initialization lifecycle hook.
   *
   * @remarks Initializes the component by updating the disabled state
   * and setting up format listeners for automatic input formatting.
   */
  ngOnInit(): void {
    this.updateDisabledState();
    this.setupFormatListener();
  }

  /**
   * Handles input property changes.
   *
   * @remarks Updates the disabled state when the control configuration changes
   * to ensure the input reflects the current configuration.
   *
   * @param {SimpleChanges} changes - Object containing the changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['controlConfig'] && !changes['controlConfig'].firstChange) {
      this.updateDisabledState();
    }
  }

  /**
   * Sets up automatic formatting for input values.
   *
   * @remarks Subscribes to form control value changes and applies formatting
   * based on the configured format pattern. Currently supports date formatting.
   */
  private setupFormatListener(): void {
    if (!this.controlConfig.format) {
      return;
    }

    const control = this.formGroup?.get(this.controlConfig.name);
    if (control) {
      // Subscribe to value changes to apply formatting as needed
      control.valueChanges.subscribe(value => {
        if (value && this.controlConfig.format) {
          // Apply format based on the specified pattern
          const formatted = this.applyFormat(value, this.controlConfig.format);
          if (formatted !== value) {
            // Only update if the formatted value is different to avoid infinite loops
            control.setValue(formatted, { emitEvent: false });
          }
        }
      });
    }
  }

  /**
   * Applies formatting to input values based on the specified format.
   *
   * @remarks Routes to specific formatting functions based on the format type.
   * Currently supports DD.MM.YYYY date formatting with extensibility for additional formats.
   *
   * @param {string} value - The input value to format
   * @param {string} format - The format pattern to apply
   * @returns {string} The formatted value
   */
  private applyFormat(value: string, format: string): string {
    // Handle date format DD.MM.YYYY
    if (format === 'DD.MM.YYYY') {
      return this.formatDateInput(value);
    }
    // Add more format handlers as needed
    return value;
  }

  /**
   * Formats input values as DD.MM.YYYY date format.
   *
   * @remarks Automatically inserts dots at appropriate positions to create
   * a properly formatted German date string. Removes non-digit characters and
   * applies formatting progressively as the user types.
   *
   * @param {string} value - The input value to format as a date
   * @returns {string} The formatted date string
   */
  private formatDateInput(value: string): string {
    // Remove any non-digit characters
    const digits = value.replace(/\D/g, '');

    // Apply DD.MM.YYYY format
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 4) {
      return `${digits.substring(0, 2)}.${digits.substring(2)}`;
    } else {
      return `${digits.substring(0, 2)}.${digits.substring(2, 4)}.${digits.substring(4, 8)}`;
    }
  }

  /**
   * Updates the disabled state of the form control.
   *
   * @remarks Enables or disables the form control based on the configuration.
   * Called during initialization and when the configuration changes.
   */
  private updateDisabledState(): void {
    const control = this.formGroup?.get(this.controlConfig.name);
    if (control) {
      if (this.controlConfig.disabled) {
        control.disable();
      } else {
        control.enable();
      }
    }
  }
}
